import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { agentAuthService } from '../services/agentAuthService';

/**
 * Protected route component that allows access to both admins and Abidjan agents.
 * Admins are redirected to /admin-login if not authenticated.
 * Agents are redirected to /agent-abidjan-login if not authenticated.
 * If either check passes, access is granted.
 */
const ProtectedAdminOrAgentRoute = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // 'checking' | 'admin' | 'agent' | 'denied'

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check admin first
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) {
          const { data: adminUser } = await supabase
            ?.from('admin_users')
            ?.select('id')
            ?.eq('email', session?.user?.email)
            ?.single();
          if (adminUser) {
            setStatus('admin');
            return;
          }
        }

        // Check agent
        const agentSession = await agentAuthService?.getSession();
        if (agentSession) {
          setStatus('agent');
          return;
        }

        // Neither admin nor agent
        setStatus('denied');
      } catch (error) {
        console.error('Auth check error:', error);
        setStatus('denied');
      }
    };

    checkAuth();
  }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (status === 'admin' || status === 'agent') {
    return children;
  }

  // Denied — redirect to admin login by default (admin dashboard links here)
  return <Navigate to="/admin-login" replace state={{ from: location }} />;
};

export default ProtectedAdminOrAgentRoute;
